/**
 * Command-queue bridge. Drains the zustand command queue for any command
 * whose entityType (and targetPath, for route-filter) matches the current
 * CustomDash page. Commands the drawer emits just before `navigate()` wait
 * in the queue until the target page mounts this bridge, then fire.
 *
 * Key property: commands survive navigation. The old synchronous-handler
 * design (and its `bindingRef` predecessor) lost commands emitted before
 * the target page's bridge had registered.
 */
import mixpanel from 'mixpanel-browser';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';

import { BuildADashTrackingEvents } from '@revelio/core';
import { getDiscoveryTargetForPageId } from '@revelio/core/global-ai-assistant/discovery-targets';

import { convertTabContentToLLM } from '../CustomDash/components/AIAssistant/convert-tab-content-to-llm';
import {
  applyLlmTabContentToTabDispatch,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import { getEntityTypeFromPrimaryFilter } from '../CustomDash/components/EditableGrid/getEntityTypeFromPrimaryFilter';
import { useEditMode } from '../CustomDash/components/EditModeProvider/EditModeProvider';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../CustomDash/components/PageConfigProvider/constants';
import {
  usePageConfig,
  useTabContentConfig,
} from '../CustomDash/components/PageConfigProvider/PageConfigProvider';
import { PageType } from '../CustomDash/components/PageConfigProvider/types';
import { buildAiSearchCurrentFilterContext } from '../CustomDash/filters/ai-search/buildAiSearchCurrentFilterContext';
import {
  getActivityTaxonomyIdFromActiveFilterMap,
  getRoleTaxonomyIdForAiSearch,
  getSkillTaxonomyIdFromActiveFilterMap,
} from '../CustomDash/filters/ai-search/getAiSearchTaxonomyFromFilterMap';
import { useApplyAiSearchFilters } from '../CustomDash/filters/ai-search/useApplyAiSearchFilters';
import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../CustomDash/filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../CustomDash/filters/hooks/useEffectiveFilters';
import { FilterId, FilterType } from '../CustomDash/filters/types/filters';
import { isDefaultUntitledName } from '../CustomDash/utils/generate-unique-title';
import {
  getPrimaryFilterIdForScreenerV2Page,
  isScreenerV2Page,
} from '../CustomDash/utils/routing-helpers';
import { buildCurrentPageScope } from './build-current-page-scope';
import { formatClarificationQuestionsAsText } from './format-clarification-questions';
import {
  NarrationCollector,
  setActiveNarrationCollector,
} from './ga-narration-collection-context';
import {
  type CustomDashAssistantEntityType,
  type GlobalAssistantEntityType,
  toCustomDashAssistantEntityType,
} from './global-ai-assistant-api';
import {
  type GlobalAiCommand,
  shouldPairTaxonomyWithResponse,
  useGlobalAiCommandStore,
} from './global-ai-assistant-command-store';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';
import {
  type CachedPageStreamResult,
  readCachedPageStream,
  writeCachedPageStream,
} from './global-ai-assistant-page-stream-cache';
import {
  isScreenerAiOutput,
  isTalentDiscoveryFilterResponse,
  isTalentDiscoveryPublicPath,
} from './global-ai-assistant-route-filter-kind';
import { dashboardStreamEventToProgress } from './global-ai-page-gen-progress';
import {
  runDashboardPageStreamCreate,
  runDashboardPageStreamUpdate,
} from './global-ai-run-page-stream';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';
import {
  summarizeAppliedChartFilters,
  summarizeUnappliedChartFilters,
  withChartScope,
} from './turn-outcome-digest';
import { useGlobalAIAssistantFeatureEnabled } from './use-global-ai-assistant-feature-enabled';
import { useGlobalAssistantTaxonomy } from './use-global-ai-assistant-taxonomy';
import { usePublishCurrentPageFilters } from './use-publish-current-page-filters';

function getPrimaryFilterForAssistantEntity(
  entityType: CustomDashAssistantEntityType
) {
  return PRIMARY_FILTER_BY_AI_ENTITY[entityType];
}

type GlobalAiBridgeCommandMatchContext = {
  editMode: boolean;
  entityTypeNow: GlobalAssistantEntityType | null;
  pathname: string;
};

function getPreviousPromptsForUpdateCommand(
  command: Extract<
    GlobalAiCommand,
    { kind: 'startCustomPageStream'; mode: 'update' }
  >
): string[] {
  const currentPrompt = command.prompt.trim();
  return (
    command.conversation?.turns
      .filter((turn) => turn.role === 'user')
      .map((turn) => turn.content.trim())
      .filter(Boolean)
      .filter((prompt) => prompt !== currentPrompt)
      .slice(-3) ?? []
  );
}

function shouldGlobalAiBridgeHandleCommand(
  command: GlobalAiCommand,
  { editMode, entityTypeNow, pathname }: GlobalAiBridgeCommandMatchContext
) {
  if (entityTypeNow === null || command.entityType !== entityTypeNow) {
    return false;
  }

  if (command.kind === 'applyRouteFilters') {
    return globalAssistantPathsMatch(pathname, command.targetPath);
  }

  if (command.kind === 'applySingleChart') {
    return editMode && globalAssistantPathsMatch(pathname, command.targetPath);
  }

  if (command.mode === 'update') {
    // Update mode intentionally targets the currently mounted edit page.
    // Unlike draft-opening commands, it has no future destination path.
    return editMode;
  }

  return editMode && globalAssistantPathsMatch(pathname, command.targetPath);
}

export function GlobalAIAssistantBridge() {
  const enabled = useGlobalAIAssistantFeatureEnabled();
  const ctx = useGlobalAIAssistantOptional();
  const { pathname } = useLocation();
  const {
    config: pageConfig,
    dispatch: pageDispatch,
    discardChanges,
    hasUnsavedChanges,
  } = usePageConfig();
  const { editMode } = useEditMode();
  const {
    config: tabContent,
    items: tabItems,
    dispatch,
    dateFilterId,
  } = useTabContentConfig();
  const effectiveFilters = useEffectiveFilters();
  const dateFilter =
    effectiveFilters.find((filter) => filter.id === dateFilterId) ??
    effectiveFilters.find((filter) => filter.id === FilterId.DATE_RANGE);
  const effectiveDateFilter =
    dateFilter?.type === FilterType.DATE_RANGE ||
    dateFilter?.type === FilterType.DATE
      ? dateFilter
      : null;
  const isDateResolved =
    effectiveDateFilter?.type === FilterType.DATE_RANGE
      ? Boolean(effectiveDateFilter.min && effectiveDateFilter.max)
      : Boolean(effectiveDateFilter?.value);
  // `geographyTaxonomyId` is the tree the page's OWN geography ids were resolved
  // under — the same value `useRequestGeographyTaxonomyId()` wraps. It has to reach
  // the published scope, because the labels are stamped with it and the server reads
  // it to tell which tree it is looking at.
  const { activeFilterMap, geographyTaxonomyId } = useFilterContext();
  // TODO(backend-adaptive): this readiness gate on the two publish effects
  // below exists to avoid publishing a stale pre-resolution taxonomy into the
  // store (which the drawer would then use for a wasted fetch under the wrong
  // cache key). Once the backend accepts "adaptive" and the drawer no longer
  // needs to mirror the adaptive-resolved value, it can go. See the
  // refinement-pass docblock in `global-ai-assistant-drawer.tsx` for context.
  //
  // The reducer's flag, never the per-taxonomy preference hooks: those return
  // `undefined` both while an adaptive taxonomy resolves and once it settles
  // on the default `0`, so an adaptive user on the default can never read as
  // ready and the scope is never published.
  const isTaxonomyResolved = useIsTaxonomyResolved();
  // Used for the page-stream cache key: composes the bridge's live
  // rolesTax/skillsTax (from activeFilterMap) with the user's saved-pref
  // adaptive identity. Same reasoning as the filter-prefetch cache —
  // adaptive re-resolution should not fracture the cache into duplicate
  // entries. See `AssistantTaxonomy` docstring.
  const prefsTaxonomyByEntity = useGlobalAssistantTaxonomy();
  const { applyAiSearchFilters, applyScreenerAiSearchFilters } =
    useApplyAiSearchFilters();
  const activeRolesTax = useMemo(
    () => getRoleTaxonomyIdForAiSearch(activeFilterMap),
    [activeFilterMap]
  );
  const activeSkillsTax = useMemo(
    () => getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap),
    [activeFilterMap]
  );
  const activeActivitiesTax = useMemo(
    () => getActivityTaxonomyIdFromActiveFilterMap(activeFilterMap),
    [activeFilterMap]
  );

  const getIntentTitleFromUnknown = useCallback(
    (content: unknown): string | null => {
      if (typeof content !== 'object' || content === null) {
        return null;
      }
      const candidate = (content as { title?: unknown }).title;
      return typeof candidate === 'string' ? candidate : null;
    },
    []
  );

  // Latest summary read by async narration callbacks (which resolve after the
  // command handler's closure was created and need the freshest value).
  const conversationSummaryRef = useRef(ctx?.conversationSummary);
  conversationSummaryRef.current = ctx?.conversationSummary;
  // Collector for the in-flight page-create narration. The drawer is the
  // GA host, but the generated widgets and their settled data live in THIS
  // bridge's CustomDash page tree, so collection is armed here. Disposed when
  // a new page-create starts, and on narration completion — nowhere else, so
  // a long-lived bridge never leaks a stale collector.
  const narrationCollectorRef = useRef<NarrationCollector | null>(null);

  const publishChannelEvent = useGlobalAiCommandStore(
    (state) => state.publishChannelEvent
  );

  const armPageNarration = useCallback(
    (params: {
      prompt: string;
      generatedItemIds: string[];
      conversationSummaryText?: string;
      /**
       * Page-level applied-filter labels summarized from the page build's
       * AI-search `filters` event (same source + summarizer as the inline
       * chart). Stamped onto every collected widget digest so grounded
       * narration knows the widgets are filtered and cannot claim the value
       * is unfiltered or re-offer an already-applied filter.
       */
      filtersApplied?: string[];
      /** The resolved filters the page could not apply (its exclusions). */
      filtersNotApplied?: string[];
    }) => {
      if (params.generatedItemIds.length === 0) {
        return;
      }
      const { filtersApplied, filtersNotApplied } = params;
      narrationCollectorRef.current?.dispose();
      const collector = new NarrationCollector({
        expectedItemIds: params.generatedItemIds,
        deadlineTiers: [
          // Majority settled → narrate at 5s instead of waiting on a
          // straggler that usually never settles before the hard timeout. The
          // page is already visible during this wait, so the only cost is a
          // slightly-less-complete digest in exchange for less narration
          // latency on the common (warm) case.
          {
            atMs: 5_000,
            minReported: Math.max(
              1,
              Math.ceil(params.generatedItemIds.length / 2)
            ),
          },
          // Cold-page middle tier: SOMETHING settled → narrate at 12s from
          // what we have instead of riding the 30s backstop. The
          // partial-aware narration contract (client tools) keeps the model
          // honest about still-loading charts.
          { atMs: 12_000, minReported: 1 },
        ],
        // Hard backstop matched to the single-chart settle budget
        // (CHART_SETTLE_TIMEOUT_MS = 30s). A shorter backstop routinely
        // completed with ZERO digests on cold pages — the model then narrated
        // the page blind (inventing absent "compensation"/"sentiment"
        // sections). The collector still completes as soon as every widget
        // reports, so warm/cache-hit pages are unaffected.
        timeoutMs: 30_000,
        onComplete: (digests) => {
          narrationCollectorRef.current = null;
          setActiveNarrationCollector(null);
          // Publish the collected widget digests so the tool loop can await
          // them before narrating. The loop's next text segment IS the
          // narration now — the bridge deliberately does NOT self-narrate or
          // write any transcript message here (that produced a second,
          // duplicate narration).
          publishChannelEvent('pageCollection', {
            digests:
              filtersApplied?.length || filtersNotApplied?.length
                ? digests.map((digest) =>
                    withChartScope(digest, {
                      filtersApplied,
                      filtersNotApplied,
                    })
                  )
                : digests,
            prompt: params.prompt,
            expectedCount: params.generatedItemIds.length,
          });
        },
      });
      narrationCollectorRef.current = collector;
      setActiveNarrationCollector(collector);
    },
    [publishChannelEvent]
  );

  // Keep the current page name in a ref so the title-overwrite guard can
  // read it synchronously during SSE event handling without retriggering
  // the command-queue effect on every keystroke in the page header.
  const pageNameRef = useRef(pageConfig.name);
  useEffect(() => {
    pageNameRef.current = pageConfig.name;
  }, [pageConfig.name]);

  const dispatchGeneratedPageTitle = useCallback(
    (args: { title: string | null; mode: 'create' | 'update' }) => {
      if (args.mode !== 'create') {
        return;
      }
      const title = args.title?.trim();
      if (!title) {
        return;
      }
      // Only adopt the AI-generated title while the page is still using its
      // auto-generated default
      // ("Untitled" / "Untitled 2" / ...). This keeps user-renamed pages
      // from being silently overwritten — including via the cache-hit
      // path, which applies a cached title after an async filter step.
      if (!isDefaultUntitledName(pageNameRef.current)) {
        return;
      }
      pageDispatch({ type: 'UPDATE_PAGE_TITLE', payload: title });
    },
    [pageDispatch]
  );

  /**
   * Screener v2 public pages (company/geo/role Discovery) have no
   * `tabContent.primaryFilter` — the dimension is implicit from the template.
   * Without this branch `entityTypeNow` is null, the command-queue effect
   * bails, and global-assistant route filters (including screener endpoint
   * responses) never apply.
   */
  const entityTypeNow = (() => {
    if (pageConfig.type === PageType.REPORT) {
      return null;
    }
    const discovery = getDiscoveryTargetForPageId(pageConfig.revelioPageId);
    // App-level Discovery surfaces have no primary filter to claim commands by.
    if (discovery?.filterKind === 'tdV2') {
      return discovery.entityType;
    }
    const fromPrimary = getEntityTypeFromPrimaryFilter(
      tabContent.primaryFilter
    );
    // Skill and activity pages are not assistant entities yet — the page
    // catalog and dash-meta intents gain them in a follow-up PR — so the bridge
    // does not claim commands for them.
    if (fromPrimary && fromPrimary !== 'skill' && fromPrimary !== 'activity') {
      return fromPrimary;
    }
    if (discovery?.filterKind === 'screener') {
      return discovery.entityType;
    }
    return null;
  })();

  // Subscribe to the queue — bridge re-runs its drain effect whenever the
  // queue changes OR any dep that affects command handling changes.
  const queue = useGlobalAiCommandStore((state) => state.queue);
  const claim = useGlobalAiCommandStore((state) => state.claim);
  const complete = useGlobalAiCommandStore((state) => state.complete);
  const pruneExpired = useGlobalAiCommandStore((state) => state.pruneExpired);
  const publishActiveTaxonomy = useGlobalAiCommandStore(
    (state) => state.publishActiveTaxonomy
  );
  const publishLastAppliedFilterContext = useGlobalAiCommandStore(
    (state) => state.publishLastAppliedFilterContext
  );

  // Publish the current page's taxonomy into the store so the drawer (which
  // lives outside CustomDash and has no filter context) can use the ACTIVE
  // page taxonomy when resolving AI filters. Without this the drawer falls
  // back to saved preferences, which miss session-only overrides (e.g. the
  // user picking a taxonomy in Preferences without clicking "Set as default").
  //
  // Gated on the reducer's settled flag to avoid publishing a pre-resolution
  // value (see the TODO(backend-adaptive) note on `isTaxonomyResolved` above).
  //
  // TODO(backend-adaptive): re-evaluate the entire publish effect when
  // the backend accepts "adaptive". The drawer's primary reason for
  // reading `activeTaxonomyByEntity` is the refinement pass; once that's
  // gone, this effect's remaining use is niche session-override support.
  // See the refinement-pass docblock in `global-ai-assistant-drawer.tsx`.
  useEffect(() => {
    if (!enabled || !entityTypeNow) return;
    if (!isTaxonomyResolved) return;
    publishActiveTaxonomy(entityTypeNow, {
      rolesTax: activeRolesTax,
      skillsTax: activeSkillsTax,
      activitiesTax: activeActivitiesTax,
    });
    return () => {
      publishActiveTaxonomy(entityTypeNow, undefined);
    };
  }, [
    enabled,
    entityTypeNow,
    activeRolesTax,
    activeSkillsTax,
    activeActivitiesTax,
    isTaxonomyResolved,
    publishActiveTaxonomy,
  ]);

  /**
   * The page's PRIMARY filter, which the scope publishes even unsubmitted: on a
   * Role, Geography or Industry page it IS the subject a page-relative ask is
   * about, and the user navigated to it rather than picking it.
   *
   * Screener v2 pages (Company / Geography / Role Discovery) declare no
   * `tabContent.primaryFilter` — the dimension is implicit in the template — so
   * without the second branch those three publish no subject and every
   * page-relative read of them refuses.
   */
  const primaryFilterId =
    tabContent.primaryFilter?.id ??
    (isScreenerV2Page(pageConfig.revelioPageId)
      ? getPrimaryFilterIdForScreenerV2Page(pageConfig.revelioPageId)
      : undefined);

  // The page's ACTIVE filters as a composition scope, so a page-relative ask
  // ("how does that look for engineers?") reads THIS page's companies and
  // filters underneath it. Rebuilt only when the filter map changes; the store
  // skips the set when the emitted scope is unchanged.
  const currentPageFilters = useMemo(
    () =>
      buildCurrentPageScope(activeFilterMap, {
        ...(primaryFilterId ? { primaryFilterId } : {}),
        geographyTaxonomyId,
        effectiveDateFilter,
      }),
    [activeFilterMap, primaryFilterId, geographyTaxonomyId, effectiveDateFilter]
  );

  // Gated on taxonomy resolution for the SAME reason as the taxonomy publish
  // above, but with sharper stakes: role and skill ids are taxonomy-relative,
  // so an artifact published before the page's taxonomy settles encodes its
  // ids under the wrong tree — the server would then read a real id naming a
  // different role and answer confidently about the wrong population. Not a
  // cosmetic race. The publish/clear pair lives in its own hook so the effect
  // ORDER it depends on is testable without mounting this whole bridge.
  usePublishCurrentPageFilters({
    enabled,
    pathname,
    filters: currentPageFilters,
    isDateResolved,
    isTaxonomyResolved,
  });

  // Expose the current CustomDash UI state (edit mode + whether the page
  // already has items) to the drawer so it can include those hints in
  // backend requests (the "should we offer Update vs Create?" routing on
  // the backend keys off this). `useLayoutEffect` so it runs before the
  // drawer's next render; it's a pure ref write, no state change.
  useLayoutEffect(() => {
    if (!ctx) return;
    ctx.customDashAssistContextRef.current = {
      isInCustomDashEditMode: editMode,
      hasCurrentPageItems: tabItems.length > 0,
      hasUnsavedChanges,
      discardChanges,
    };
    return () => {
      if (!ctx) return;
      ctx.customDashAssistContextRef.current = {
        isInCustomDashEditMode: false,
        hasCurrentPageItems: false,
        hasUnsavedChanges: false,
        discardChanges: undefined,
      };
    };
  }, [ctx, discardChanges, editMode, hasUnsavedChanges, tabItems]);

  useEffect(() => {
    if (!enabled || !ctx) {
      return;
    }

    pruneExpired();

    // Find the first queued command this bridge can handle. Only 'queued'
    // commands are eligible — 'in_flight' ones are already being handled,
    // typically by a previous render of this bridge.
    const match = queue.find(
      (q) =>
        q.status === 'queued' &&
        shouldGlobalAiBridgeHandleCommand(q.command, {
          editMode,
          entityTypeNow,
          pathname,
        })
    );

    if (!match) return;
    if (!claim(match.command.requestId, pathname)) return;

    void runCommand(match.command);

    async function runCommand(command: GlobalAiCommand) {
      // Track whether any branch set a user-visible error message. The
      // outer finally only clears the status on success — so a message
      // from a catch block survives until the user takes the next action.
      let errorStatus: string | null = null;
      try {
        if (command.kind === 'applyRouteFilters') {
          if (!ctx) return;
          ctx.setGenerationStatus('Applying filters…');
          // Response was resolved by the drawer in parallel with navigation,
          // so no network call here — just apply.
          try {
            // Choose the applier by the resolved artifact's SHAPE, not by the
            // path-derived `command.filterKind`. The drawer pre-resolves filters
            // against a route-derived endpoint, so a mismatch (e.g. a screener
            // `CompanyDiscoveryAiOutput` carried on a command whose path-derived
            // `filterKind` is `tdV2`) would otherwise hand the response to the
            // wrong applier and corrupt the apply. The SHAPE wins. Only if the
            // shape is unrecognizable do we fall back to `filterKind` so nothing
            // regresses.
            const response = command.response;
            // The SHAPE wins; only fall back to the path-derived `filterKind`
            // when neither guard matches an otherwise unrecognizable artifact.
            let applyAsScreener: boolean;
            if (isScreenerAiOutput(response)) {
              applyAsScreener = true;
            } else if (isTalentDiscoveryFilterResponse(response)) {
              applyAsScreener = false;
            } else {
              applyAsScreener = command.filterKind === 'screener';
            }
            if (applyAsScreener) {
              // Screener-shaped (`filters.entity_info_selectors`). Casting from
              // the resolved union is safe: the shape guard above (or the
              // `filterKind` fallback) selected this branch.
              applyScreenerAiSearchFilters(
                response as Extract<
                  typeof response,
                  { filters: { entity_info_selectors: unknown } }
                >,
                {
                  mergeWithExistingFilters: command.merge === true,
                  source: 'global_assistant',
                }
              );
            } else {
              const tdResponse = response as Exclude<
                typeof response,
                { filters: { entity_info_selectors: unknown } }
              >;
              // Discovery refinements and explicit additive filter requests
              // preserve/merge the page's primary. A grounded standard-page
              // landing omits `merge`, so it replaces an unrelated primary.
              const shouldMergePrimarySelections =
                isTalentDiscoveryPublicPath(command.targetPath) ||
                command.merge === true;
              const primarySelectionMergeMode = shouldMergePrimarySelections
                ? 'merge'
                : 'replace';
              const routeApplyResult = await applyAiSearchFilters(tdResponse, {
                primarySelectionMergeMode,
                // A fresh result-type landing must not inherit an unrelated
                // primary from the destination's previous session. If the
                // artifact has no ranked role/geo/company selections, the
                // destination's defaults remain instead.
                preserveCurrentPrimaryWhenMissing: shouldMergePrimarySelections,
                skipTalentDiscoveryAdaptFallback: true,
                mergeWithExistingFilters: command.merge === true,
                source: 'global_assistant',
                // Pair the page's taxonomy with the response's
                // roles_tax_id/skills_tax_id (default-on; see the
                // command docblock). The response ids are only valid
                // under that taxonomy — chips, the selection list, and
                // queries must all use it.
                ...(shouldPairTaxonomyWithResponse(command.pairTaxonomy)
                  ? { pairRoleTaxonomyWithResponse: true }
                  : {}),
                // The geography twin, and NOT default-on: only a command that
                // deliberately switched the taxonomy carries it (see
                // `applyRouteFilters.adoptGeoTax`). Without it the apply drops
                // the response's geography as resolved under the wrong tree,
                // which is the right call for every other caller.
                ...(command.adoptGeoTax !== undefined
                  ? { adoptGeographyTaxonomyId: command.adoptGeoTax }
                  : {}),
              });
              // Succeeded but capped — see the `filterApplyAdjustment`
              // channel docblock in the command store.
              useGlobalAiCommandStore.getState().reportFilterApplyAdjustment({
                requestId: command.requestId,
                path: command.targetPath,
                droppedNames: routeApplyResult?.droppedSelectionNames ?? [],
              });
            }
            if (command.prompt?.trim()) {
              publishLastAppliedFilterContext(command.targetPath, {
                targetPath: command.targetPath,
                surfaceKind: 'dashboard',
                entityType: command.entityType,
                prompt: command.prompt,
              });
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_APPLIED,
              { entity_type: command.entityType }
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Apply failed';
            errorStatus = `Couldn't apply filters: ${message}`;
            // The chat turn already ended optimistically ("applying filters
            // now…"), so a consumed-but-failed apply must surface in-chat
            // too — the drawer watches this (DP4).
            useGlobalAiCommandStore.getState().reportFilterApplyFailure({
              requestId: command.requestId,
              path: command.targetPath,
            });
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED,
              {
                entity_type: command.entityType,
                global_assistant_route_filter_error: message,
              }
            );
          }
        } else if (command.kind === 'applySingleChart') {
          if (!ctx) return;
          ctx.setGenerationStatus('Applying chart…');
          try {
            const customDashEntityType = toCustomDashAssistantEntityType(
              command.entityType
            );
            if (!customDashEntityType) {
              throw new Error(
                'Custom Dash is not available for Individual Discovery.'
              );
            }
            const primaryFilterId =
              getPrimaryFilterForAssistantEntity(customDashEntityType);
            applyLlmTabContentToTabDispatch(
              dispatch,
              command.config,
              primaryFilterId
            );
            if (command.filters) {
              ctx.setGenerationStatus('Applying filters…');
              await applyAiSearchFilters(command.filters, {
                primarySelectionMergeMode: 'replace',
                skipTalentDiscoveryAdaptFallback: true,
                source: 'global_assistant',
                // Same contract as the route apply: the streamed page's role
                // ids are only valid under the taxonomy the response resolved
                // them with.
                pairRoleTaxonomyWithResponse: true,
              });
              if (command.prompt?.trim()) {
                publishLastAppliedFilterContext(command.targetPath, {
                  targetPath: command.targetPath,
                  surfaceKind: 'dashboard',
                  entityType: command.entityType,
                  prompt: command.prompt,
                });
                // Deliberately no filter-apply narration here: the model narrates
                // chart turns inline via streamed text on the loop path.
              }
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_CHART_APPLIED,
              { entity_type: command.entityType }
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Apply failed';
            errorStatus = `Couldn't apply chart: ${message}`;
          }
        } else if (command.kind === 'startCustomPageStream') {
          const commandMode = command.mode ?? 'create';
          if (!ctx) return;
          ctx.streamAbortRef.current?.abort();
          const ac = new AbortController();
          ctx.streamAbortRef.current = ac;
          const customDashEntityType = toCustomDashAssistantEntityType(
            command.entityType
          );
          if (!customDashEntityType) {
            ctx.setGenerationStatus(
              'Custom Dash is not available for Individual Discovery.'
            );
            // Keyed: a click-time build parks its own controller in this shared slot,
            // so an unkeyed clear would strand it and leave that build uncancellable.
            if (ctx.streamAbortRef.current === ac) {
              ctx.streamAbortRef.current = null;
            }
            return;
          }
          const rolesTax = getRoleTaxonomyIdForAiSearch(activeFilterMap);
          const skillsTax =
            getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap);
          const activitiesTax =
            getActivityTaxonomyIdFromActiveFilterMap(activeFilterMap);
          // The page's own live capability, not a second Unleash read:
          // `FilterProvider` keeps ACTIVITY/ACTIVITY_TAXONOMY out of the filter
          // map entirely unless the activity filter is definitively on (the
          // page's gate AND `useUnleashReady`), so membership here IS the
          // confirmed capability and can never claim a filter this page cannot
          // hold.
          const activitiesEnabled = activeFilterMap.has(
            FilterId.ACTIVITY_TAXONOMY
          );
          // Role/skill come from the live filter map above; geography does
          // NOT live on the page filter map — it is a GLOBAL user preference,
          // so it is read from saved prefs for both the update and create
          // paths below. `undefined` while the flag is off.
          const prefsTax = prefsTaxonomyByEntity(customDashEntityType);
          const geoTaxPayload =
            prefsTax.geoTax !== undefined ? { geoTax: prefsTax.geoTax } : {};

          // Update mode: patch the current page's layout in place using
          // the bounded conversation context (recent turns + summary).
          // Deliberately skips the cache — the current tab content IS
          // the state being updated, so there's no meaningful
          // prompt+taxonomy cache key.
          if (command.mode === 'update') {
            try {
              ctx.setGenerationStatus('Updating page…');
              let sawQuestions = false;
              await runDashboardPageStreamUpdate({
                newPrompt: command.prompt,
                entityType: customDashEntityType,
                currentConfig: convertTabContentToLLM({
                  ...tabContent,
                  items: tabItems,
                  grid: tabContent.grid ?? {
                    rows: DEFAULT_GRID_ROWS,
                    columns: DEFAULT_GRID_COLUMNS,
                  },
                }),
                currentFilterContext:
                  buildAiSearchCurrentFilterContext(activeFilterMap),
                previousPrompts: getPreviousPromptsForUpdateCommand(command),
                model: command.model,
                answers: command.answers,
                rolesTax,
                skillsTax,
                activitiesTax,
                ...(activitiesEnabled ? { activitiesEnabled: true } : {}),
                ...geoTaxPayload,
                conversation: command.conversation,
                dispatch,
                applyAiSearchFilters,
                onEvent: (ev) => {
                  if (ev.type === 'questions') {
                    sawQuestions = true;
                    ctx.appendTranscript({
                      id: crypto.randomUUID(),
                      role: 'assistant',
                      kind: 'clarification',
                      text: formatClarificationQuestionsAsText({
                        triggerReason: ev.content.triggerReason,
                        questions: ev.content.questions,
                        emptyFallback:
                          'I need a bit more detail to update that page.',
                      }),
                    });
                    ctx.setGenerationStatus(null);
                  } else if (ev.type === 'error') {
                    ctx.setGenerationStatus(ev.content);
                  }
                  // Publish structured progress (thinking/intent/status) so the
                  // turn runner's update subscription streams the update's live
                  // reasoning to the activity lane — same single source as the
                  // create path (so an update no longer reads as a dead "Updating
                  // page…" wait).
                  const progress = dashboardStreamEventToProgress(ev);
                  if (progress) {
                    publishChannelEvent('pageGenProgress', progress);
                  }
                },
                signal: ac.signal,
              });
              if (sawQuestions) {
                return;
              }
              mixpanel.track(
                BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
                {
                  entity_type: command.entityType,
                  global_assistant_page_stream_cache_hit: false,
                }
              );
            } catch (e) {
              errorStatus = e instanceof Error ? e.message : 'Update failed';
            } finally {
              // Keyed: a click-time build parks its own controller in this shared slot,
              // so an unkeyed clear would strand it and leave that build uncancellable.
              if (ctx.streamAbortRef.current === ac) {
                ctx.streamAbortRef.current = null;
              }
            }
            return;
          }

          // Cache-key identity from saved prefs (stable across adaptive
          // re-resolution); API payload from the bridge's live filter
          // map (reflects current page state).
          const cacheTaxonomy = {
            rolesTax,
            skillsTax,
            activitiesTax,
            rolesKey: prefsTax.rolesKey,
            skillsKey: prefsTax.skillsKey,
            activitiesKey: prefsTax.activitiesKey,
            ...geoTaxPayload,
          };
          const shouldUsePageStreamCache =
            !command.answers || command.answers.length === 0;

          // Fast path: if we've already generated a layout for this
          // prompt + entity + taxonomy within the cache TTL, skip the
          // SSE stream and apply the cached config + filters directly.
          const cached = shouldUsePageStreamCache
            ? readCachedPageStream(
                command.prompt,
                customDashEntityType,
                cacheTaxonomy,
                activitiesEnabled
              )
            : null;
          if (cached) {
            try {
              ctx.setGenerationStatus('Reusing cached layout…');
              const primaryFilterId =
                getPrimaryFilterForAssistantEntity(customDashEntityType);
              // Capture the items actually dispatched to the tab. Their ids
              // are what the widgets mount with, so the narration collector
              // MUST be armed with these exact ids — re-converting the LLM
              // content would mint fresh uuids that never match.
              const { items: dispatchedItems } =
                applyLlmTabContentToTabDispatch(
                  dispatch,
                  cached.config,
                  primaryFilterId
                );
              dispatchGeneratedPageTitle({
                title: cached.title ?? null,
                mode: commandMode,
              });
              if (cached.filters) {
                await applyAiSearchFilters(cached.filters, {
                  primarySelectionMergeMode: 'replace',
                  skipTalentDiscoveryAdaptFallback: true,
                  source: 'global_assistant',
                  // Replayed from cache, but the ids are still the response's
                  // and still taxonomy-relative — pair exactly as the live
                  // apply above does.
                  pairRoleTaxonomyWithResponse: true,
                });
              }
              armPageNarration({
                prompt: command.prompt,
                // Text items have no data-provider and never emit a digest — exclude
                // them so the collector doesn't wait out the full timeout for them.
                generatedItemIds: dispatchedItems
                  .filter((item) => item.plot?.chartType !== 'text')
                  .map((item) => item.id),
                filtersApplied: summarizeAppliedChartFilters(cached.filters),
                filtersNotApplied: summarizeUnappliedChartFilters(
                  cached.filters
                ),
                ...(conversationSummaryRef.current?.text
                  ? {
                      conversationSummaryText:
                        conversationSummaryRef.current.text,
                    }
                  : {}),
              });
              mixpanel.track(
                BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
                {
                  entity_type: command.entityType,
                  global_assistant_page_stream_cache_hit: true,
                }
              );
            } catch (e) {
              errorStatus = e instanceof Error ? e.message : 'Apply failed';
            } finally {
              // Keyed: a click-time build parks its own controller in this shared slot,
              // so an unkeyed clear would strand it and leave that build uncancellable.
              if (ctx.streamAbortRef.current === ac) {
                ctx.streamAbortRef.current = null;
              }
            }
            return;
          }

          // Miss — run the stream, capture config + filters for the
          // cache along the way. Only write cache on successful
          // completion (no throw). Multiple config events may arrive
          // during the stream; the last one wins (matches how the
          // stream runner applies them — each call replaces the
          // previous dispatch).
          try {
            ctx.setGenerationStatus('Generating page…');
            let capturedConfig: CachedPageStreamResult['config'] | undefined;
            let capturedFilters: CachedPageStreamResult['filters'] | undefined;
            let capturedTitle: CachedPageStreamResult['title'] | undefined;
            let sawQuestions = false;
            // First-title-wins guard. `pageNameRef` is synced via effect,
            // so it cannot block subsequent intents that arrive within the
            // same SSE chunk (processed synchronously). Mirror the hooks'
            // synchronous `conversationTitleRef` pattern with a local flag.
            let titleDispatched = false;
            const { dispatchedItems } = await runDashboardPageStreamCreate({
              prompt: command.prompt,
              entityType: customDashEntityType,
              model: command.model,
              answers: command.answers,
              rolesTax,
              skillsTax,
              activitiesTax,
              ...(activitiesEnabled ? { activitiesEnabled: true } : {}),
              ...geoTaxPayload,
              conversation: command.conversation,
              dispatch,
              applyAiSearchFilters,
              onEvent: (ev) => {
                if (ev.type === 'intent') {
                  const intentTitle = getIntentTitleFromUnknown(ev.content);
                  // First-title-wins for the cache: keep the earliest
                  // non-null title so a cached replay dispatches the same
                  // page title the user originally saw, and so a later
                  // titleless intent can't clear it (which would make the
                  // cache-hit path bail on subsequent runs).
                  capturedTitle = capturedTitle ?? intentTitle ?? undefined;
                  if (!titleDispatched && intentTitle) {
                    titleDispatched = true;
                    dispatchGeneratedPageTitle({
                      title: intentTitle,
                      mode: commandMode,
                    });
                  }
                }
                if (ev.type === 'config') {
                  capturedConfig = ev.content;
                } else if (ev.type === 'filters') {
                  capturedFilters = ev.content;
                } else if (ev.type === 'questions') {
                  sawQuestions = true;
                  ctx.appendTranscript({
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    kind: 'clarification',
                    text: formatClarificationQuestionsAsText({
                      triggerReason: ev.content.triggerReason,
                      questions: ev.content.questions,
                      emptyFallback:
                        'I need a bit more detail to build that page.',
                    }),
                  });
                  ctx.setGenerationStatus(null);
                } else if (ev.type === 'error') {
                  ctx.setGenerationStatus(ev.content);
                }
                // Publish structured progress (thinking/intent/status) for the
                // activity lane. The turn runner subscribes and drives the
                // activity-lane string through the progress channel — single
                // source, so we no longer set it directly here for `status`.
                const progress = dashboardStreamEventToProgress(ev);
                if (progress) {
                  publishChannelEvent('pageGenProgress', progress);
                }
              },
              signal: ac.signal,
            });
            if (sawQuestions) {
              return;
            }
            if (capturedConfig) {
              // Reuse the items the stream runner actually dispatched to the
              // tab — their ids are what the widgets mounted with. Re-converting
              // `capturedConfig` here would mint fresh, non-matching uuids and
              // the collector would time out with an empty digest.
              armPageNarration({
                prompt: command.prompt,
                // Text items have no data-provider and never emit a digest — exclude
                // them so the collector doesn't wait out the full timeout for them.
                generatedItemIds: dispatchedItems
                  .filter((item) => item.plot?.chartType !== 'text')
                  .map((item) => item.id),
                filtersApplied: summarizeAppliedChartFilters(capturedFilters),
                filtersNotApplied:
                  summarizeUnappliedChartFilters(capturedFilters),
                ...(conversationSummaryRef.current?.text
                  ? {
                      conversationSummaryText:
                        conversationSummaryRef.current.text,
                    }
                  : {}),
              });
            }
            if (capturedConfig && shouldUsePageStreamCache) {
              writeCachedPageStream(
                command.prompt,
                customDashEntityType,
                cacheTaxonomy,
                activitiesEnabled,
                {
                  config: capturedConfig,
                  ...(capturedFilters ? { filters: capturedFilters } : {}),
                  ...(capturedTitle ? { title: capturedTitle } : {}),
                }
              );
            }
            mixpanel.track(
              BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE,
              {
                entity_type: command.entityType,
                global_assistant_page_stream_cache_hit: false,
              }
            );
          } catch (e) {
            errorStatus = e instanceof Error ? e.message : 'Generation failed';
          } finally {
            // Keyed: a click-time build parks its own controller in this shared slot,
            // so an unkeyed clear would strand it and leave that build uncancellable.
            if (ctx.streamAbortRef.current === ac) {
              ctx.streamAbortRef.current = null;
            }
          }
        }
      } finally {
        complete(command.requestId);
        // Only clear the status on success. If a branch set `errorStatus`,
        // surface it to the user — they'll see why the flow failed instead
        // of being returned to a silent empty page.
        if (errorStatus === null) {
          ctx?.setGenerationStatus(null);
        } else {
          ctx?.setGenerationStatus(errorStatus);
        }
      }
    }
  }, [
    enabled,
    editMode,
    ctx,
    entityTypeNow,
    pathname,
    tabContent,
    tabItems,
    dispatch,
    activeFilterMap,
    applyAiSearchFilters,
    applyScreenerAiSearchFilters,
    queue,
    claim,
    complete,
    pruneExpired,
    publishLastAppliedFilterContext,
    prefsTaxonomyByEntity,
    dispatchGeneratedPageTitle,
    getIntentTitleFromUnknown,
    armPageNarration,
    publishChannelEvent,
  ]);

  return null;
}
